import { createLogger } from '../../../../utils/logger';

const logger = createLogger('FormErrorHandling');

/**
 * Standard error format for form errors
 */
export interface FormError {
  /** Error message */
  message: string;
  /** Context where the error occurred */
  context: string;
  /** Original error if available */
  originalError?: unknown;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Creates a standardized form error object
 *
 * @param error - The error that occurred
 * @param context - The context in which the error occurred
 * @param metadata - Additional metadata about the error
 * @returns A standardized form error object
 */
export const createFormError = (
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): FormError => {
  const errorMessage = error instanceof Error ? error.message : String(error);

  const formError: FormError = {
    message: errorMessage,
    context,
    originalError: error,
    metadata,
  };

  // Log the error
  logger.error(`Error in ${context}:`, formError);

  return formError;
};

/**
 * Handles a form error by creating a standardized error object and returning user-friendly error info
 *
 * @param error - The error that occurred
 * @param context - The context in which the error occurred
 * @param metadata - Additional metadata about the error
 * @returns User-friendly error information
 */
export const handleFormError = (
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): { message: string; fieldErrors?: Record<string, string> } => {
  const formError = createFormError(error, context, metadata);

  // Extract field errors if available
  const fieldErrors =
    error instanceof Error &&
    'fieldErrors' in error &&
    typeof (error as any).fieldErrors === 'object'
      ? ((error as any).fieldErrors as Record<string, string>)
      : undefined;

  return {
    message: formError.message,
    fieldErrors,
  };
};
